import { FRONTEND_RIGHTS } from 'shared/modules/permission';
import React, { useContext, useEffect, useRef } from 'react';
import Validators from 'components/validators';
import { SENDER } from 'constants/lang-constants';
import { AuthenticationContext } from 'modules/authentication';
import { useHistory } from 'react-router-dom';
import { Translate } from 'lang/_dictionary';
import { hasRight } from 'state/helpers';
import { isReferenceUpdate, isReferenceIdMatch } from 'components/ReferenceHandling';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { CustomerContext } from 'shared/state';
import pagesConfigurations from 'categories/configurations';
import replaceRegexWithCustomerToken from 'newRouting/helpers/replace-regex-with-customer-token.helper';
import { AppPage } from 'shared/components';
import {
  Button,
  Card,
  Form,
  FormCheckbox,
  FormDropdown,
  FormTextInput,
} from '@sosafe-platform-engineering/fe-lib-ui-react';
import { getSenderTypeKey } from 'components/Utils';
import { useTranslation } from 'react-i18next';
import useInitModuleTranslations from 'lang/useInitModuleTranslations';
import { addTemplate } from 'state/actions/templates.service';
import translations from './lang';
import {
  loadArcheTypes,
  loadTags,
  loadCategoryTags,
  loadBranchTags,
  loadFunctionTags,
} from '../../services/base.service';
import { getTagsByVectorFromArray } from '../../hooks/useTemplateEditData';
import TagInput from '../../components/templates-tag-input/TagInput';

import 'scss/tags.scss';

const LANG = Translate();

export default function TemplatesAddPage() {
  useInitModuleTranslations({ translations, namespace: 'templates-add' });
  const { t } = useTranslation('templates-add');
  const { state } = useContext(AuthenticationContext);
  const user = state?.user;
  const { customer, loading: loadingCustomer } = useContext(CustomerContext);
  const formRef = useRef();

  const { tags, archetypes, categoryTags, branchTags, functionTags } = useSelector(
    (reduxState) => reduxState.base,
    shallowEqual,
  );
  const { loading, reference } = useSelector((reduxState) => reduxState.template, shallowEqual);

  const reduxDispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    if (hasRight(FRONTEND_RIGHTS.FE_ARCHETYPES, user)) {
      reduxDispatch(loadArcheTypes(user));
    }
    if (hasRight(FRONTEND_RIGHTS.FE_TAGS, user)) {
      reduxDispatch(loadTags());
      reduxDispatch(loadCategoryTags());
      reduxDispatch(loadBranchTags());
      reduxDispatch(loadFunctionTags());
    }
  }, []);

  return (
    <DeprecatedAddTemplatePage
      user={user}
      customer={customer}
      tags={tags}
      archetypes={archetypes}
      categoryTags={categoryTags}
      branchTags={branchTags}
      functionTags={functionTags}
      loading={loading || loadingCustomer}
      reference={reference}
      history={history}
      addTemplate={(templateData) => reduxDispatch(addTemplate(user, customer, templateData))}
      t={t}
      formRef={formRef}
    />
  );
}

class DeprecatedAddTemplatePage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      name: '',
      difficulty: 0,
      sender_type: 0,
      context: 0,
      unique: false,
      show: false,
      parent_id: null,
      psychoTags: this.getTagsByVector(1),
      technoTags: this.getTagsByVector(2),
      selectedPsychoTags: [],
      selectedTechnoTags: [],
      selectedCategoryTags: [],
      selectedBranchTags: [],
      selectedFunctionTags: [],
      enabled: true,
      archetype_id: null,
      conversation_case: true,
    };
  }

  componentDidUpdate(prevProps) {
    if (isReferenceUpdate(prevProps, this.props)) {
      if (isReferenceIdMatch(this.props.reference, 'TEMPLATE_ADD_SUCCESS')) {
        this.props.history.push(
          replaceRegexWithCustomerToken({
            path: pagesConfigurations.templates?.path,
            token: this.props.customer.token,
          }),
        );
      }
    }
  }

  render() {
    const { loading, t, formRef } = this.props;
    const sortedSender = SENDER.sort((a, b) => a.value.localeCompare(b.value));

    const difficultyOptions = [
      { value: '0', label: LANG.misc.easy },
      { value: '1', label: LANG.misc.medium },
      { value: '2', label: LANG.misc.hard },
    ];

    const senderTypeOptions = [];
    sortedSender.map((data) => senderTypeOptions.push({ value: getSenderTypeKey(data.value), label: data.value }),
    );

    const contextOptions = [
      { value: '0', label: LANG.misc.private },
      { value: '1', label: LANG.misc.business },
    ];

    const archetypeOptions = [];
    this.props.archetypes.map((data) => archetypeOptions.push({ value: data.id, label: data[this.props.user.language] }),
    );

    return (
      <AppPage showHead pageKey="templates-add">
        <div className="row">
          <div className="col-12 col-sm-10 col-md-8 col-lg-8">
            <Form
              initialValues={{
                name: this.state.name,
                difficulty: this.state.difficulty,
                unique: this.state.unique,
                conversation_case: this.state.conversation_case,
                sender_type: this.state.sender_type,
                context: this.state.context,
                archetype_id: this.state.archetype_id,
                parent_id: this.state.parent_id,
                enabled: this.state.enabled,
                show: this.state.show,
                selectedPsychoTags: this.state.selectedPsychoTags,
                selectedTechnoTags: this.state.selectedTechnoTags,
                selectedCategoryTags: this.state.selectedCategoryTags,
                selectedBranchTags: this.state.selectedBranchTags,
                selectedFunctionTags: this.state.selectedFunctionTags,
              }}
              enableReinitialize
              validateOnChange={false}
              onSubmit={(values) => this.onSubmit(values)}
              innerRef={formRef}
            >
              <div className="d-flex flex-row flex-wrap">
                <Card className={'mt-3'}>
                  <Card.Body>
                    <FormTextInput
                      name="name"
                      label={LANG.edit_add_template_page.name}
                      maxlength={70}
                      className="mb-3"
                      variant="border"
                      validator={(val) => {
                        if (!Validators.required(val).valid) {
                          return t('field-required');
                        }
                        return undefined;
                      }}
                      required
                    />
                    <FormDropdown
                      name="difficulty"
                      className="mb-3"
                      options={difficultyOptions}
                      label={LANG.edit_add_template_page.difficulty}
                      variant="border"
                      placeholder={difficultyOptions[0].label}
                    />
                    <FormCheckbox
                      name="conversation_case"
                      className="mb-1"
                      label={LANG.edit_add_template_page.conversation_case}
                      tooltip={{
                        description: LANG.edit_add_template_page.conversation_case_tooltip,
                      }}
                    />
                    <FormCheckbox name="unique" className="mb-2" label={LANG.misc.unique} />
                    <FormDropdown
                      name="sender_type"
                      className="mb-3"
                      options={senderTypeOptions}
                      label={LANG.edit_add_template_page.sender_type}
                      variant="border"
                      placeholder={senderTypeOptions[0].label}
                    />
                    <FormDropdown
                      name="context"
                      className="mb-3"
                      options={contextOptions}
                      label={LANG.edit_add_template_page.context}
                      variant="border"
                      placeholder={contextOptions[0].label}
                    />
                    {hasRight(FRONTEND_RIGHTS.FE_ARCHETYPES, this.props.user) && (
                      <FormDropdown
                        name="archetype_id"
                        className="mb-3"
                        options={archetypeOptions}
                        label={LANG.edit_add_template_page.archetype}
                        variant="border"
                        placeholder={'------'}
                      />
                    )}
                    {hasRight(FRONTEND_RIGHTS.FE_PARENT_ID, this.props.user) && (
                      <FormTextInput
                        name="parent_id"
                        label={LANG.edit_add_template_page.parent_id}
                        maxlength={70}
                        className="mb-3"
                        variant="border"
                      />
                    )}
                    <TagInput
                      name={'selectedPsychoTags'}
                      language={this.props.user.language}
                      label={LANG.edit_add_template_page.psycho_label}
                      tags={this.state.psychoTags}
                      selectedTags={this.state.selectedPsychoTags}
                      callback={this.psychoTagInputCallback}
                    />
                    <TagInput
                      name={'selectedTechnoTags'}
                      language={this.props.user.language}
                      label={LANG.edit_add_template_page.techno_label}
                      tags={this.state.technoTags}
                      selectedTags={this.state.selectedTechnoTags}
                      callback={this.technoTagInputCallback}
                    />
                    <TagInput
                      name="selectedCategoryTags"
                      language={this.props.user.language}
                      label={LANG.edit_add_template_page.category_label}
                      tags={this.props.categoryTags}
                      selectedTags={this.state.selectedCategoryTags}
                      callback={this.categoryTagInputCallback}
                    />
                    <TagInput
                      name="selectedBranchTags"
                      language={this.props.user.language}
                      label={LANG.edit_add_template_page.branch_label}
                      tags={this.props.branchTags}
                      selectedTags={this.state.selectedBranchTags}
                      callback={this.branchTagInputCallback}
                    />
                    <TagInput
                      name="selectedFunctionTags"
                      language={this.props.user.language}
                      label={LANG.edit_add_template_page.function_label}
                      tags={this.props.functionTags}
                      selectedTags={this.state.selectedFunctionTags}
                      callback={this.functionTagInputCallback}
                    />
                    <FormCheckbox
                      name="enabled"
                      className="mb-1"
                      label={LANG.edit_add_template_page.enabled}
                    />
                    <FormCheckbox
                      name="show"
                      className="mb-2"
                      label={LANG.edit_add_template_page.show}
                    />
                    <Button
                      className="u-sizeFull"
                      type="submit"
                      disabled={loading}
                      ariaLabel="submit"
                    >
                      {loading ? LANG.misc.loading : LANG.misc.create}
                    </Button>
                  </Card.Body>
                </Card>
              </div>
            </Form>
          </div>
        </div>
      </AppPage>
    );
  }

  getTagsByVector(vector) {
    const { tags } = this.props;
    return getTagsByVectorFromArray(vector, tags);
  }

  mergeArrays(tagList1, tagList2) {
    if (tagList1) {
      return tagList1.concat(tagList2);
    }
    return tagList1;
  }

  updateFormState() {
    const { values } = this.props.formRef.current;
    const updateState = {
      ...this.state,
      name: values.name,
      difficulty: values.difficulty,
      sender_type: values.sender_type,
      context: values.context,
      unique: values.unique,
      show: values.show,
      parent_id: values.parent_id,
      selectedPsychoTags: this.state.selectedPsychoTags,
      selectedTechnoTags: this.state.selectedTechnoTags,
      selectedCategoryTags: this.state.selectedCategoryTags,
      selectedBranchTags: this.state.selectedBranchTags,
      selectedFunctionTags: this.state.selectedFunctionTags,
      enabled: values.enabled,
      archetype_id: values.archetype_id,
      conversation_case: values.conversation_case,
    };
    this.setState(updateState);
  }

  technoTagInputCallback = (props) => {
    this.updateFormState();
    this.setState({ selectedTechnoTags: props.tags });
  };

  psychoTagInputCallback = (props) => {
    this.updateFormState();
    this.setState({ selectedPsychoTags: props.tags });
  };

  categoryTagInputCallback = (props) => {
    this.updateFormState();
    this.setState({ selectedCategoryTags: props.tags });
  };

  branchTagInputCallback = (props) => {
    this.updateFormState();
    this.setState({ selectedBranchTags: props.tags });
  };

  functionTagInputCallback = (props) => {
    this.updateFormState();
    this.setState({ selectedFunctionTags: props.tags });
  };

  onSubmit(values) {
    const template = {
      name: values.name,
      difficulty: parseInt(values.difficulty, 10),
      sender_type: parseInt(values.sender_type, 10),
      context: parseInt(values.context, 10),
      tags: this.mergeArrays(values.selectedPsychoTags, values.selectedTechnoTags),
      category_tags: values.selectedCategoryTags,
      branche_tags: values.selectedBranchTags,
      function_tags: values.selectedFunctionTags,
      subdomain: null,
      enabled: values.enabled,
      conversation_case: values.conversation_case,
      unique: values.unique,
      show: values.show,
    };

    const archetype_id = parseInt(values.archetype_id, 10);
    if (archetype_id > 0) {
      template.archetype_id = archetype_id;
    } else {
      template.archetype_id = null;
    }

    const parent_id = parseInt(values.parent_id, 10);
    if (parent_id > 0) {
      template.parent_id = parent_id;
    } else {
      template.parent_id = null;
    }

    this.props.addTemplate(template);
  }
}
